import {
  ButtonDS,
  DataCard,
  Spacer,
  TextCapitalized,
  triggerToast,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { MultipleDrivers } from "@qivia/ui/src/assets/assets";
import { DriversToSendFormModal } from "./modals/driversToSendFormModal";
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  selectDriversList,
  driversListAsync,
  selectDriversListStatus,
} from "../drivers/driversSlice";
import { selectCompany } from "../homeSlice";
import {
  driverElectroCompatibilityFormsAsync,
  selectDriverElectroCompatibilityFormsStatus,
  getDriverElectroCompatibilityFormsAsync,
  selectDriverElectroCompatibilityForms,
} from "./electrificationSlice";
import {
  DriversListFormDisplayedRow,
  GetDriverElectroCompatibilityFormsResponse,
} from "./electrificationApi";
import {
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDbStatus,
} from "../vehicles/vehiclesSlice";
import "../../../i18n/i18n";

type VehiclesHeaderType = "electrifiableVehicles" | "shareOfElectrifiable";
const vehiclesHeaderData: VehiclesHeaderType[] = [
  "electrifiableVehicles",
  "shareOfElectrifiable",
];
type DriversHeaderType =
  | "superElectroCompatible"
  | "averageElectroCompatibleScore"
  | "shareOfDriversAnalyzed";

const driversHeaderDatas: DriversHeaderType[] = [
  "superElectroCompatible",
  "averageElectroCompatibleScore",
  "shareOfDriversAnalyzed",
];

export const ElectrificationHeader = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const drivers = useAppSelector(selectDriversList);
  const company = useAppSelector(selectCompany);
  const driverElectroCompatibilityFormsStatus = useAppSelector(
    selectDriverElectroCompatibilityFormsStatus,
  );
  const driversListStatus = useAppSelector(selectDriversListStatus);
  const listAllVehiclesInDbStatus = useAppSelector(
    selectListAllVehiclesInDbStatus,
  );
  const [isModalDriversFormVisible, setIsModalDriversFormVisible] =
    useState<boolean>(false);

  const onSubmit = useCallback(
    (response: DriversListFormDisplayedRow[]) => {
      if (!company) {
        return;
      }
      const drivers = response.map((driver) => ({
        uuid: driver.uuid ?? "",
        phone: driver.phone ?? "",
      }));
      void dispatch(
        driverElectroCompatibilityFormsAsync({
          drivers,
          company: company.uuid,
        }),
      );
    },
    [company, dispatch],
  );

  useEffect(() => {
    if (driverElectroCompatibilityFormsStatus === "success") {
      triggerToast(
        t("electrification.header.title.survey.success") || "",
        "valid",
      );
    }
    if (driverElectroCompatibilityFormsStatus === "failed") {
      triggerToast(
        t("electrification.header.title.survey.failed") || "",
        "error",
      );
    }
  }, [dispatch, driverElectroCompatibilityFormsStatus, t]);

  useEffect(() => {
    if (company && driversListStatus === "idle") {
      void dispatch(driversListAsync(company.uuid));
    }
  }, [company, dispatch, driversListStatus]);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [company, dispatch, listAllVehiclesInDbStatus]);

  useEffect(() => {
    if (company && driverElectroCompatibilityFormsStatus === "idle") {
      void dispatch(getDriverElectroCompatibilityFormsAsync());
    }
  }, [company, dispatch, driverElectroCompatibilityFormsStatus]);

  if (!params.tab) {
    return;
  }

  return (
    <StyledHeader>
      <Spacer y={1.5} />
      <StyledFlex>
        <Spacer x={1.5} />
        <StyledLeft>
          <TextCapitalized>
            {t(`electrification.header.title.${params.tab}`)}
          </TextCapitalized>
          <TextCapitalized>
            {t(`electrification.header.title.${params.tab}.part2`)}
          </TextCapitalized>
          <Spacer y={1} />
          <StyledSubtitle>
            <TextCapitalized>
              {t(`electrification.header.subtitle.${params.tab}`)}
            </TextCapitalized>
          </StyledSubtitle>
          {params.tab === "drivers" && (
            <>
              <Spacer y={1.5} />
              <StyledButtonDiv>
                <ButtonDS
                  text={t("electrification.header.title.survey")}
                  format={"hug"}
                  buttonType={"primary"}
                  sizeButton={"S"}
                  onClick={() => setIsModalDriversFormVisible(true)}
                />
              </StyledButtonDiv>
            </>
          )}
        </StyledLeft>
        <Spacer x={1} />
        <StyledRight>
          <ContentHeader tab={params.tab} />
        </StyledRight>
        <Spacer x={1.5} />
      </StyledFlex>
      <Spacer y={1.5} />
      <DriversToSendFormModal
        visible={isModalDriversFormVisible}
        onCloseModal={() => setIsModalDriversFormVisible(false)}
        drivers={drivers}
        onSubmit={onSubmit}
      />
    </StyledHeader>
  );
};

const getVehiclesDataHeader = (): Record<string, number> => {
  return {
    electrifiableVehicles: 0,
    shareOfElectrifiable: 0,
  };
};

const getDriversDataHeader = (
  driverElectroCompatibilityForms: GetDriverElectroCompatibilityFormsResponse,
): Record<string, number> => {
  //TODO LATER, CALCULATE DATA
  console.log(driverElectroCompatibilityForms);
  return {
    superElectroCompatible: 0, //?
    averageElectroCompatibleScore: 0, //Faire moyenne du score électro compatibilité + Score de conduite
    shareOfDriversAnalyzed: 0, //nombre de conducteurs ayant répondus à la campagne
  };
};

const ContentHeader = (payload: { tab: string }) => {
  const { t } = useTranslation();
  const driverElectroCompatibilityForms = useAppSelector(
    selectDriverElectroCompatibilityForms,
  );

  const data =
    payload.tab === "vehicles"
      ? getVehiclesDataHeader()
      : getDriversDataHeader(driverElectroCompatibilityForms);

  const headerData =
    payload.tab === "vehicles" ? vehiclesHeaderData : driversHeaderDatas;
  const titleElement =
    payload.tab === "vehicles" ? (
      <img
        src={Car}
        style={{ width: "2.2rem", filter: "grayscale(100%)" }}
        alt="Car"
      />
    ) : (
      <MultipleDrivers />
    );

  return (
    <>
      {headerData.map((v, index) => {
        return (
          <StyledFlex key={index + v}>
            {index !== 0 && <Spacer x={1.5} />}
            <DataCard
              width={19}
              titleElement={titleElement}
              title={t(`electrification.header.${v}`)}
              titleHeight={typographies["Body/XS"]}
              backgroundColor="white"
              content={
                <StyledFlex>
                  {data[v] === 0 ? "NaN" : data[v]}
                  <Spacer x={0.5} />
                  {t(`electrification.header.${v}.unit`)}
                </StyledFlex>
              }
            />
          </StyledFlex>
        );
      })}
    </>
  );
};

const StyledHeader = styled.div`
  border-radius: 0.5rem;
  background: ${colors["colors/surfaces/background/background_level1"]};
  width: 100%;
  font-size: 1.75rem;
  font-weight: 400;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledLeft = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 25rem;
`;

const StyledButtonDiv = styled.div`
  display: flex;
  width: 10rem;
`;

const StyledRight = styled.div`
  display: flex;
  width: 100%;
  overflow: auto;
`;

const StyledSubtitle = styled.div`
  display: flex;
  max-width: 50rem;
  ${typographies["Body/S"]}
`;
